import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'utils/classnames';
import { useIsomorphicLayoutEffect, useWindowSize } from '@navi-toolbox/hooks';
import { FromDesktop } from 'common/components/adaptive';
import { scrollToPosition } from './utils/scroll-to-position';
import { Container, wrapperStyles, SafariScrollGap } from './styles';
import { CarouselProps, OnControlClickProps } from './types';
import { CarouselControl } from './carousel-control';
import { overscroll, calcChildFromProps, onCarouselScroll, renderExtra } from './utils/carousel-utils';

export const Carousel: React.FC<CarouselProps> = ({
  children,
  className,
  containerClassName,
  fullScroll,
  testMock,
  onChange,
  dataQa,
  hideControls,
  isBannerCarousel,
  stepQuantity = 1,
  ...props
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [inScrollNow, setInScrollNow] = useState(false);
  const [isMobileSafari, setIsMobileSafari] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { width } = useWindowSize();
  const [currentChild, setCurrentChild] = useState(isBannerCarousel && children ? children.length : 0);
  const [gap, setGap] = useState(12);
  const [scrollTimer, setScrollTimer] = useState<NodeJS.Timeout | null>(null);

  const onControlClick = useCallback(
    (clickProps: OnControlClickProps = {}) => {
      let { cancelAnimations, customGap } = clickProps;
      const element = containerRef.current;
      const carouselGap = customGap || gap;
      if ((clickProps.direction && inScrollNow) || !element) return;
      if (element) {
        const { offset, stop } = overscroll({
          element,
          isBannerCarousel,
          currentChild,
          setCurrentChild,
          gap: carouselGap,
          onChange,
          fullScroll,
          setInScrollNow,
          direction: clickProps.direction,
          stepQuantity,
        });
        if (stop) return;
        if (onChange) onChange(offset, clickProps.direction);
        const scrollSpeed = cancelAnimations ? { maxDuration: 0, minDuration: 0 } : { maxDuration: 300 };
        scrollToPosition(
          { x: offset, y: 0 },
          { ...scrollSpeed, elementToScroll: element, easingFn: 'easeInOutQuint' },
        ).then(() => {
          setInScrollNow(false);
          setCurrentChild(
            calcChildFromProps({
              direction: clickProps.direction,
              currentChild,
              length: element.children.length,
              fullScroll,
            }),
          );
        });
      }
    },
    [gap, inScrollNow, isBannerCarousel, currentChild, onChange, fullScroll, stepQuantity],
  );
  const checkCanScroll = useCallback(() => {
    const element = containerRef.current;
    if (element) {
      const { scrollLeft, offsetWidth, scrollWidth } = element;
      const indentation = 1;
      if (scrollLeft < indentation) {
        setCanScrollLeft(false);
      } else {
        setCanScrollLeft(true);
      }
      if (scrollWidth - offsetWidth - indentation < scrollLeft) {
        setCanScrollRight(false);
      } else {
        setCanScrollRight(true);
      }
    }
  }, []);
  useIsomorphicLayoutEffect(() => {
    if (!isBannerCarousel) {
      checkCanScroll();
    } else {
      setCanScrollLeft(true);
      setCanScrollRight(true);
    }
  }, [checkCanScroll]);
  useIsomorphicLayoutEffect(() => {
    const customGap = 12;
    setGap(customGap);
    setIsMobileSafari(
      customGap === 12 &&
        navigator.userAgent.indexOf('Chrome') === -1 &&
        navigator.userAgent.indexOf('Safari') > -1,
    );
    onControlClick({ cancelAnimations: true, customGap });
  }, [width]);

  const scrollProps = {
    isBanner: isBannerCarousel,
    currentElement: currentChild,
    setCurrentElement: setCurrentChild,
    onChangeFn: onChange,
    elementGap: gap,
    timer: scrollTimer,
    setTimer: setScrollTimer,
    scrollState: inScrollNow,
    setScrollState: setInScrollNow,
    checkScroll: checkCanScroll,
  };
  return (
    <div className={cn(className, wrapperStyles)} {...props}>
      <>
        <FromDesktop>
          {canScrollLeft && !hideControls && (
            <CarouselControl
              label={t('ui.buttons.prev')}
              direction="left"
              onClick={() => onControlClick({ direction: 'left' })}
              data-qa={`${dataQa}-left`}
            />
          )}
        </FromDesktop>
        <Container
          style={{ '--grid-gap': `${gap}px` }}
          ref={containerRef}
          onScroll={onCarouselScroll(scrollProps)}
          inScrollNow={inScrollNow}
          className={cn({ extendMobilePadding: isBannerCarousel }, containerClassName)}
          data-qa={dataQa}
        >
          {}
          {isBannerCarousel && children && renderExtra(children, 'prev')}
          {children}
          {}
          {isBannerCarousel && children && renderExtra(children, 'next')}
          {!isBannerCarousel && isMobileSafari && <SafariScrollGap />}
        </Container>
        <FromDesktop>
          {canScrollRight && !hideControls && (
            <CarouselControl
              label={t('ui.buttons.next')}
              direction="right"
              onClick={() => onControlClick({ direction: 'right' })}
              data-qa={`${dataQa}-right`}
            />
          )}
        </FromDesktop>
      </>
    </div>
  );
};
